/**
 * Copyright (c) 2025 Grigoris Paschalidis
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, distribution or use
 * of this software, via any medium, is strictly prohibited.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Users, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { calculateDaysBetween, LEAVE_TYPES, getUsedDaysByTypeForYears, getUsedDaysByLeaveYear, getLeaveYear } from '../utils/vacationUtils';
import DepartmentFilter from './DepartmentFilter';

// Always shows last cycle / current cycle / next cycle — recalculated on every
// load, so this never needs manual updating as years pass.
const getYearWindow = () => {
  const current = getLeaveYear(new Date());
  return [current - 1, current, current + 1];
};
const YEARS = getYearWindow();

const MANAGER_DEFAULT_DEPARTMENTS = {
  7:  [2, 4, 6, 7, 14, 15, 16],
  28: [21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 39],
};

const TeamView = ({ userDatabase, vacationRequests, departments, holidays = [], annualBalances = [], currentUser }) => {
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedYears, setSelectedYears] = useState([getYearWindow()[1]]);
  const [showOlderYears, setShowOlderYears] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [expandedUsers, setExpandedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Older years are opt-in via the "Παλαιότερα έτη" toggle below — keeps the
  // default pill row to the standard 3-year window, but lets managers dig
  // back further when they actually need to.
  const visibleYears = showOlderYears
    ? [YEARS[0] - 2, YEARS[0] - 1, ...YEARS]
    : YEARS;

  useEffect(() => {
  if (!departments || departments.length === 0) return;
  if (selectedDepartments.length > 0) return;

  const explicitDepts = MANAGER_DEFAULT_DEPARTMENTS[currentUser?.id];
  if (explicitDepts) {
    const valid = explicitDepts.filter(id => departments.some(d => d.id === id));
    setSelectedDepartments(valid.length > 0 ? valid : departments.map(d => d.id));
  } else {
    const managedDeptIds = [...new Set(
      userDatabase
        .filter(u => (u.manager_id || u.managerId) === currentUser.id)
        .map(u => u.department_id || u.departmentId)
        .filter(Boolean)
    )];
    setSelectedDepartments(
      managedDeptIds.length > 0 ? managedDeptIds : departments.map(d => d.id)
    );
  }
}, [departments]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleYear = (year) => {
    setSelectedYears(prev =>
      prev.includes(year)
        ? prev.length === 1 ? prev : prev.filter(y => y !== year)
        : [...prev, year].sort()
    );
  };

  const getAllowanceForYear = (userId, year) => {
    if (!annualBalances || annualBalances.length === 0) return 0;
    const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
    if (pivoted) {
      const key = 'balance_' + year;
      if (pivoted[key] !== undefined) return Number(pivoted[key]);
    }
    const raw = annualBalances.find(b =>
      Number(b.user_id || b.userId) === Number(userId) &&
      Number(b.cycle_start_year || b.year) === year
    );
    if (raw && raw.total_allowed !== undefined) return Number(raw.total_allowed);
    return 0;
  };

  // Single pass over vacationRequests, building userId -> year -> { approved, granted }.
  // Previously every getUserYearData() call independently re-scanned the
  // entire vacationRequests array up to 4 times (approved + granted, for
  // both the target year and year-1's overflow check) — multiplied across
  // every user, every selected year, and every render (stats row, sort
  // comparator, and each user card all called it separately). With a
  // realistic employee count that added up to thousands of full-array scans
  // per click. This index is built once whenever the underlying data
  // actually changes, and every other computation below just reads from it.
  const yearDataIndex = useMemo(() => {
    const index = new Map(); // userId -> { [year]: { approved, granted } }

    const ensure = (userId, year) => {
      if (!index.has(userId)) index.set(userId, {});
      const byYear = index.get(userId);
      if (!byYear[year]) byYear[year] = { approved: 0, granted: 0 };
      return byYear[year];
    };

    if (vacationRequests) {
      for (const req of vacationRequests) {
        if (req.status !== 'approved') continue;
        const startDate = req.start_date || req.startDate;
        if (!startDate) continue;
        const parts = startDate.toString().split('T')[0].split('-').map(Number);
        const cycleYear = parts[1] - 1 < 3 ? parts[0] - 1 : parts[0];

        const userId = Number(req.user_id || req.userId);
        const isGranted = req.manager_granted || req.managerGranted;
        // Do NOT default an absent leave_type to 'vacation' — some records
        // (e.g. older unpaid leave) are stored with a blank leave_type, and
        // defaulting them to 'vacation' silently counted them as approved
        // vacation days AND inflated the overflow basis. An empty type must
        // match neither 'vacation' nor 'mandatory'. This mirrors how
        // YearlyBalanceView and getUsedDays resolve the type.
        const type = (req.leave_type || req.leaveType || req.type || '').toLowerCase();
        const days = calculateDaysBetween(startDate, req.end_date || req.endDate, holidays);

        // Mirrors getUsedDays in vacationUtils.js exactly, so this index and
        // the expandable per-type breakdown below (which calls
        // getUsedDaysByTypeForYears -> getUsedDays directly) can never
        // disagree on what counts as "approved" vs "granted" again:
        //   - 'approved' = approved, NOT granted, leave_type === 'vacation'
        //   - 'granted'  = approved, granted, leave_type !== 'unpaid'
        //     (granted-unpaid is tracked separately elsewhere, not counted
        //     here, matching getGrantedDaysForYear's own !isUnpaid check)
        if (isGranted) {
          if (type !== 'unpaid') {
            ensure(userId, cycleYear).granted += days;
          }
        } else if (type === 'vacation') {
          ensure(userId, cycleYear).approved += days;
        }
      }
    }

    return index;
  }, [vacationRequests, holidays]);

  const getApprovedDaysForYear = (userId, year) => {
    return yearDataIndex.get(Number(userId))?.[year]?.approved || 0;
  };

  const getGrantedDaysForYear = (userId, year) => {
    return yearDataIndex.get(Number(userId))?.[year]?.granted || 0;
  };

  const getOverflowFromPrev = (userId, year) => {
    const prevAllowed = getAllowanceForYear(userId, year - 1);
    if (prevAllowed === 0) return 0;
    // Call the SAME canonical function UserInfoBar and RequestModal use, so
    // the previous-cycle "used" (vacation + mandatory) is guaranteed
    // identical across every screen — no parallel reimplementation to drift.
    const prevUsed = getUsedDaysByLeaveYear(userId, vacationRequests, year - 1, holidays);
    const over = prevUsed - prevAllowed;
    return over > 0 ? over : 0;
  };

  const getUserYearData = (userId, year) => {
    const allowed = getAllowanceForYear(userId, year);
    const rawApproved = getApprovedDaysForYear(userId, year);
    const granted = getGrantedDaysForYear(userId, year);
    const approved = allowed > 0 ? Math.min(rawApproved, allowed) : rawApproved;
    const overflowFromPrev = getOverflowFromPrev(userId, year);
    const remaining = Math.max(0, allowed - approved - granted - overflowFromPrev);
    return { allowed, approved, granted, remaining, overflow: rawApproved - approved, overflowFromPrev };
  };

  const filteredUsers = userDatabase.filter(user => {
    const departmentId = user.department_id || user.departmentId;
    const inDept = selectedDepartments.includes(departmentId);
    const matchesSearch = !searchTerm || (user.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    return inDept && matchesSearch;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '', 'el-GR');
    if (sortBy === 'department') {
      const dA = departments.find(d => d.id === (a.department_id || a.departmentId));
      const dB = departments.find(d => d.id === (b.department_id || b.departmentId));
      return (dA?.name || '').localeCompare(dB?.name || '', 'el-GR');
    }
    if (sortBy === 'remaining_days') {
      const primaryYear = selectedYears[selectedYears.length - 1];
      return getUserYearData(b.id, primaryYear).remaining - getUserYearData(a.id, primaryYear).remaining;
    }
    return 0;
  });

  const usersByDepartment = selectedDepartments.map(deptId => ({
    department: departments.find(d => d.id === deptId),
    users: sortedUsers.filter(u => (u.department_id || u.departmentId) === deptId)
  })).filter(g => g.department && g.users.length > 0);

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text">Επισκόπηση Αδειών Ομάδας</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-bg text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="name">Κατά όνομα</option>
            <option value="department">Κατά Τμήμα</option>
            <option value="remaining_days">Κατά Υπολειπόμενες</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
            <input
              type="text"
              placeholder="Αναζήτηση υπαλλήλου..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-bg text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-52"
            />
          </div>
          <DepartmentFilter
            departments={departments}
            selectedDepartments={selectedDepartments}
            onDepartmentChange={setSelectedDepartments}
            className="w-full sm:min-w-[200px]"
          />
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-600 dark:text-jewel-text">Χρονολογία:</span>
          {visibleYears.map(year => (
            <button
              key={year}
              onClick={() => toggleYear(year)}
              className={'px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all ' +
                (selectedYears.includes(year)
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white dark:bg-jewel-bg border-gray-300 dark:border-jewel-border text-gray-500 dark:text-jewel-text hover:border-blue-400')}
            >
              {year}
            </button>
          ))}
          <button
            onClick={() => setShowOlderYears(prev => !prev)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text underline transition-colors"
          >
            {showOlderYears ? 'Απόκρυψη παλαιότερων' : 'Παλαιότερα έτη'}
          </button>
        </div>
        <p className="text-xs text-gray-400 dark:text-jewel-text-muted mt-1">(1 Απριλίου – 31 Μαρτίου)</p>
      </div>

      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">Υπάλληλοι</h4>
          <p className="text-2xl font-bold text-blue-600 dark:text-jewel-teal-tint">{sortedUsers.length}</p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">Τμήματα</h4>
          <p className="text-2xl font-bold text-purple-600 dark:text-jewel-ruby-tint">{usersByDepartment.length}</p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">{'Σύνολο Εγκρίσεων (' + selectedYears.join(', ') + ')'}</h4>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {sortedUsers.reduce((sum, u) =>
              sum + selectedYears.reduce((s, y) => {
                const d = getUserYearData(u.id, y);
                return s + d.approved + d.overflowFromPrev;
              }, 0), 0)}
          </p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm">{'Σύνολο Χορηγήσεων (' + selectedYears.join(', ') + ')'}</h4>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {sortedUsers.reduce((sum, u) =>
              sum + selectedYears.reduce((s, y) => s + getGrantedDaysForYear(u.id, y), 0), 0)}
          </p>
        </div>
      </div>

      {usersByDepartment.map(({ department, users }) => (
        <div key={department.id} className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: department.color }} />
            <h3 className="font-semibold text-gray-700 dark:text-jewel-text">{department.name}</h3>
            <span className="text-xs text-gray-400 dark:text-jewel-text-muted">({users.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {users.map(user => {
              const isExpanded = expandedUsers.includes(user.id);

              return (
                <div key={user.id} className="border border-gray-200 dark:border-jewel-border rounded-lg p-4 bg-white dark:bg-jewel-bg hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                      style={{ backgroundColor: department.color }}
                    >
                      {(user.name || '').split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-800 dark:text-jewel-text text-sm truncate">{user.name}</h4>
                        {user.role === 'manager' && <Shield className="h-3 w-3 text-blue-600 dark:text-jewel-teal-tint flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-jewel-text-muted truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {selectedYears.map(year => {
                      const { allowed, approved, granted, remaining, overflowFromPrev } = getUserYearData(user.id, year);
                      const used = approved + granted + overflowFromPrev;
                      const pct = allowed > 0 ? Math.min((used / allowed) * 100, 100) : 0;
                      return (
                        <div key={year} className="bg-gray-50 dark:bg-jewel-bg/50 rounded-lg p-2.5">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[9px] font-bold text-black dark:text-jewel-text">{'1 Απριλίου ' + year + ' – 31 Μαρτίου ' + (year + 1)}</span>
                            <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' +
                              (remaining === 0 ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' :
                               remaining <= 5 ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400' :
                               'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400')}>
                              {remaining} απομένουν
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-1 text-center mb-2">
                            <div>
                              <p className="text-[10px] text-black dark:text-jewel-text uppercase">Σύνολο</p>
                              <p className="text-sm font-bold text-gray-700 dark:text-jewel-text">{allowed}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-black dark:text-jewel-text uppercase">Εγκρίθηκαν</p>
                              <p className="text-sm font-bold text-blue-600 dark:text-jewel-teal-tint">{approved + overflowFromPrev}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-black dark:text-jewel-text uppercase">Χορηγήθηκαν</p>
                              <p className="text-sm font-bold text-purple-600 dark:text-jewel-ruby-tint">{granted}</p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-jewel-surface rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full transition-all duration-300"
                              style={{
                                width: pct + '%',
                                backgroundColor: pct > 80 ? '#EF4444' : pct > 60 ? '#F59E0B' : department.color
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted text-center mt-1">{Math.round(pct)}% χρησιμοποιημένο</p>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => toggleUserExpand(user.id)}
                    className="w-full mt-3 flex items-center justify-center space-x-1 text-xs text-blue-600 dark:text-jewel-teal-tint hover:text-blue-700 dark:hover:text-jewel-teal-tint py-1"
                  >
                    <span>{isExpanded ? 'Απόκρυψη' : 'Εμφάνιση'} ανά τύπο άδειας</span>
                    {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-jewel-border space-y-3">
                      {selectedYears.map(year => {
                        const yearUsedByType = getUsedDaysByTypeForYears(user.id, vacationRequests, [year], holidays);
                        const grantedThisYear = getGrantedDaysForYear(user.id, year);
                        const hasAnyData = Object.entries(yearUsedByType).some(([t, v]) => v > 0) || grantedThisYear > 0;
                        return (
                          <div key={year}>
                            <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mb-1.5">
                              {'1 Απριλίου ' + year + ' – 31 Μαρτίου ' + (year + 1)}
                            </p>
                            {!hasAnyData && (
                              <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted italic px-2">Καμία άδεια</p>
                            )}
                            <div className="space-y-1">
                              {Object.entries(LEAVE_TYPES).map(([type, info]) => {
                                let used = type === 'mandatory' ? grantedThisYear : (yearUsedByType[type] || 0);
                                const yrAllowance = getAllowanceForYear(user.id, year);
                                let overflow = 0;
                                if (type === 'vacation' && yrAllowance > 0 && used > yrAllowance) {
                                  overflow = used - yrAllowance;
                                  used = yrAllowance;
                                }
                                if (used === 0 && overflow === 0) return null;
                                return (
                                  <div key={type} className="flex items-center justify-between text-xs p-2 rounded" style={{ backgroundColor: info.color + '10' }}>
                                    <div className="flex items-center space-x-1">
                                      <span>{info.icon}</span>
                                      <span className="text-gray-600 dark:text-jewel-text">{info.label}:</span>
                                    </div>
                                    <span className="font-bold" style={{ color: info.color }}>{used} ημέρες</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {sortedUsers.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-jewel-text-muted">
          <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Δεν βρέθηκαν υπάλληλοι</h3>
          <p>Δεν υπάρχουν υπάλληλοι στα επιλεγμένα τμήματα.</p>
        </div>
      )}
    </div>
  );
};

export default TeamView;